import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { TradeCard } from "./TradeCard";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface JobTrade {
  id: string;
  trade_code: string;
  status: string;
  subtotal: number;
  trades?: {
    name: string;
    icon: string;
  };
}

interface Trade {
  code: string;
  name: string;
  icon: string;
}

interface JobTradesOverviewProps {
  jobId: string;
  orgId: string;
  jobTrades: JobTrade[];
  propertyType: string;
  onRefresh: () => void;
  onSelectTrade: (tradeId: string) => void;
}

export const JobTradesOverview = ({
  jobId,
  orgId,
  jobTrades,
  propertyType,
  onRefresh,
  onSelectTrade,
}: JobTradesOverviewProps) => {
  const queryClient = useQueryClient();
  const [showAddTrade, setShowAddTrade] = useState(false);
  const [availableTrades, setAvailableTrades] = useState<Trade[]>([]);
  const [selectedTrade, setSelectedTrade] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const handleOpenAddTrade = async () => {
    // Fetch available trades
    const { data: allTrades } = await supabase
      .from("trades")
      .select("*")
      .eq("is_active", true)
      .order("name");

    // Filter out already added trades
    const existingTradeCodes = jobTrades.map((jt) => jt.trade_code);
    const available = (allTrades || []).filter(
      (t) => !existingTradeCodes.includes(t.code)
    );

    setAvailableTrades(available);
    setShowAddTrade(true);
  };

  const handleAddTrade = async () => {
    if (!selectedTrade) {
      toast.error("Please select a trade");
      return;
    }

    setLoading(true);
    try {
      const { data: newTrade, error } = await supabase
        .from("job_trades")
        .insert({
          job_id: jobId,
          org_id: orgId,
          trade_code: selectedTrade,
          status: "new",
          subtotal: 0,
        })
        .select()
        .single();

      if (error) throw error;

      // Create timeline event
      await supabase.from("job_timeline").insert({
        job_id: jobId,
        org_id: orgId,
        event_type: "note",
        description: `Added ${selectedTrade} trade to project`,
      });

      toast.success("Trade added successfully");
      setShowAddTrade(false);
      setSelectedTrade("");
      
      // Invalidate and refetch job trades query
      await queryClient.invalidateQueries({ queryKey: ["job-trades", jobId] });
      onRefresh();
    } catch (error: any) {
      console.error("Error adding trade:", error);
      toast.error(error.message || "Failed to add trade");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTrade = async (tradeId: string) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from("job_trades")
        .delete()
        .eq("id", tradeId);

      if (error) throw error;

      // Create timeline event
      await supabase.from("job_timeline").insert({
        job_id: jobId,
        org_id: orgId,
        event_type: "note",
        description: "Removed trade from project",
      });

      toast.success("Trade deleted successfully");
      
      // Invalidate and refetch job trades query
      await queryClient.invalidateQueries({ queryKey: ["job-trades", jobId] });
      onRefresh();
    } catch (error: any) {
      console.error("Error deleting trade:", error);
      toast.error(error.message || "Failed to delete trade");
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = jobTrades.reduce((sum, jt) => sum + (jt.subtotal || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Trades</h2>
          <p className="text-muted-foreground">
            Manage individual trade scopes for this project
          </p>
        </div>
        <Button onClick={handleOpenAddTrade} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Trade
        </Button>
      </div>

      {/* Trade Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {jobTrades.map((jobTrade) => (
          <TradeCard
            key={jobTrade.id}
            jobTrade={jobTrade}
            propertyType={propertyType}
            onViewDetails={() => onSelectTrade(jobTrade.id)}
            onDelete={handleDeleteTrade}
          />
        ))}
      </div>

      {/* Total Amount */}
      {jobTrades.length > 1 && (
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
          <span className="text-lg font-semibold">Combined Total</span>
          <span className="text-2xl font-bold">${totalAmount.toLocaleString()}</span>
        </div>
      )}

      {/* Add Trade Dialog */}
      <Dialog open={showAddTrade} onOpenChange={setShowAddTrade}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Trade to Project</DialogTitle>
            <DialogDescription>
              Select an additional trade scope to add to this project
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="trade">Trade Type</Label>
              <Select value={selectedTrade} onValueChange={setSelectedTrade}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a trade" />
                </SelectTrigger>
                <SelectContent>
                  {availableTrades.map((trade) => (
                    <SelectItem key={trade.code} value={trade.code}>
                      <span className="flex items-center gap-2">
                        <span>{trade.icon}</span>
                        <span>{trade.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowAddTrade(false)}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddTrade}
                className="flex-1"
                disabled={loading || !selectedTrade}
              >
                {loading ? "Adding..." : "Add Trade"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
